import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  Paper,
  CircularProgress,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../../services/apiService';
import { API_ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

const ClaimProviderInvite = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { login } = useAuth();

  const [invitedEmail, setInvitedEmail] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(!!token);
  const [inviteInvalid, setInviteInvalid] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [tosAccepted, setTosAccepted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState(null); // null | 'loading' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  // Meghívott email előtöltése — a szolgáltató lecserélheti arra, amivel be akar lépni
  useEffect(() => {
    if (!token) return;
    apiService.get(API_ENDPOINTS.AUTH.PROVIDER_INVITE_INFO(token))
      .then((response) => {
        setInvitedEmail(response?.data?.email || '');
        setEmail(response?.data?.email || '');
        setBusinessName(response?.data?.businessName || '');
      })
      .catch(() => setInviteInvalid(true))
      .finally(() => setInviteLoading(false));
  }, [token]);

  const normalizedEmail = email.trim().toLowerCase();
  const emailChanged = !!invitedEmail && normalizedEmail !== invitedEmail;

  if (inviteLoading) {
    return (
      <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!token || inviteInvalid) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            Érvénytelen vagy hiányzó meghívó link. Kérjük, keresse admin kapcsolattartóját egy új linkért.
          </Alert>
          <Button component={Link} to="/" variant="contained">
            Vissza a főoldalra
          </Button>
        </Paper>
      </Container>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(normalizedEmail)) {
      setStatus('error');
      setErrorMessage('Kérjük, adjon meg érvényes email címet.');
      return;
    }
    if (emailChanged && normalizedEmail !== confirmEmail.trim().toLowerCase()) {
      setStatus('error');
      setErrorMessage('A két email cím nem egyezik.');
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      setStatus('error');
      setErrorMessage('A jelszónak legalább 6 karakter hosszúnak kell lennie.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setStatus('error');
      setErrorMessage('A két jelszó nem egyezik.');
      return;
    }
    if (!tosAccepted) {
      setStatus('error');
      setErrorMessage('A partneri ÁSZF elfogadása kötelező a folytatáshoz.');
      return;
    }

    setStatus('loading');
    try {
      const response = await apiService.post(API_ENDPOINTS.AUTH.PROVIDER_INVITE_CLAIM, {
        token,
        email: normalizedEmail,
        newPassword,
        tosAccepted,
      });

      // Az új cím csak megerősítés után lép életbe — addig a meghívott címmel lépünk be
      const loginEmail = response?.data?.email || invitedEmail;
      const pendingEmail = response?.data?.pendingEmail;
      let pendingNote = '';
      if (pendingEmail) {
        pendingNote = ` Megerősítő linket küldtünk a(z) ${pendingEmail} címre — a rá kattintás után ezzel tud majd belépni, addig a(z) ${loginEmail} címmel.`;
      } else if (emailChanged) {
        pendingNote = ` A(z) ${normalizedEmail} címre nem sikerült megerősítő levelet küldeni, ezért egyelőre a(z) ${loginEmail} címmel tud belépni. Kérjük, jelezze a hello@wedigo.hu címen.`;
      }
      const loginResult = await login(loginEmail, newPassword);

      if (loginResult.success) {
        navigate('/profil/szolgaltato', {
          state: { message: `Üdvözöljük! Kérjük, tekintse át és hagyja jóvá a profilját.${pendingNote}` },
        });
      } else {
        navigate('/login', {
          state: { message: `Jelszava beállítva. Most már bejelentkezhet a(z) ${loginEmail} címmel.${pendingNote}` },
        });
      }
    } catch (err) {
      setStatus('error');
      const msg = err?.message || '';
      if (msg.toLowerCase().includes('lejárt') || msg.toLowerCase().includes('érvénytelen')) {
        setErrorMessage('A meghívó link érvénytelen vagy lejárt. Kérjük, keresse admin kapcsolattartóját egy új linkért.');
      } else {
        setErrorMessage(msg || 'Hiba történt. Kérjük, próbálja újra.');
      }
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Profil véglegesítése
        </Typography>
        <Typography variant="body2" align="center" sx={{ mb: 3 }}>
          <Link to={`/szolgaltatoi-udvozlo?token=${encodeURIComponent(token)}`}>← Vissza a bemutatkozóhoz</Link>
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
          Elkészítettük {businessName ? <strong>{businessName}</strong> : 'a'} profilját. A folytatáshoz adja meg, melyik email címmel szeretne belépni, állítsa be a jelszavát és fogadja el a partneri ÁSZF-et — utána átnézheti és jóváhagyhatja a profil tartalmát.
        </Typography>

        {status === 'error' && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setStatus(null)}>
            {errorMessage}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email cím (ezzel fog bejelentkezni)"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={status === 'loading'}
            required
            helperText={emailChanged
              ? 'Erre a címre megerősítő linket küldünk — a csere csak a link megnyitása után lép életbe, addig a meghívott címmel tud belépni.'
              : 'Ha más címet szeretne használni a belépéshez, írja át.'}
            sx={{ mb: 2 }}
          />

          {emailChanged && (
            <TextField
              fullWidth
              label="Email cím megerősítése"
              type="email"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              disabled={status === 'loading'}
              required
              sx={{ mb: 2 }}
            />
          )}

          <TextField
            fullWidth
            label="Jelszó"
            type={showPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={status === 'loading'}
            required
            helperText="Minimum 6 karakter"
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword((p) => !p)} edge="end" tabIndex={-1}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Jelszó megerősítése"
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={status === 'loading'}
            required
            sx={{ mb: 2 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={tosAccepted}
                onChange={(e) => setTosAccepted(e.target.checked)}
                disabled={status === 'loading'}
              />
            }
            label={
              <Typography variant="body2">
                Elfogadom a{' '}
                <Link to="/partneri-aszf" target="_blank" rel="noopener noreferrer">
                  partneri ÁSZF-et
                </Link>
              </Typography>
            }
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={status === 'loading'}
            sx={{ py: 1.5, mb: 2 }}
          >
            {status === 'loading' ? <CircularProgress size={24} color="inherit" /> : 'Jelszó beállítása és folytatás'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ClaimProviderInvite;
